import { useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, Trash2 } from "lucide-react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CameraCapture } from "@/components/CameraCapture";

export const photoSrc = (user) => (user?.photo_url ? `${API.replace(/\/api$/, "")}${user.photo_url}` : null);

export const initialsOf = (name) =>
  (name || "TM").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");

export const ProfilePhoto = ({ size = 76 }) => {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const src = photoSrc(user);

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/auth/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser(data);
      setCameraOpen(false);
      toast.success("Photo added to your One Talanta ID");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save photo");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/auth/profile/photo");
      setUser(data);
      toast.success("Photo removed");
    } catch (err) {
      toast.error("Could not remove photo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="profile-avatar">
      <div className="h-full w-full rounded-2xl overflow-hidden bg-talanta-gold/20 border-2 border-white/20 flex items-center justify-center">
        {src ? (
          <img data-testid="profile-avatar-img" src={src} alt={user.name || "Profile photo"} className="h-full w-full object-cover" />
        ) : (
          <span data-testid="profile-avatar-initials" className="font-display font-extrabold text-talanta-gold" style={{ fontSize: size * 0.36 }}>
            {initialsOf(user.name)}
          </span>
        )}
        {busy && !cameraOpen && <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><Loader2 className="h-5 w-5 text-white animate-spin" /></div>}
      </div>
      <button
        data-testid="photo-capture-btn"
        onClick={() => setCameraOpen(true)}
        disabled={busy}
        aria-label={src ? "Retake photo" : "Take photo"}
        className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full bg-talanta-red text-white shadow-lg flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-60"
      >
        <Camera className="h-4 w-4" />
      </button>
      {src && (
        <button
          data-testid="photo-remove-btn"
          onClick={remove}
          disabled={busy}
          aria-label="Remove photo"
          className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white/90 text-talanta-red shadow flex items-center justify-center hover:bg-white"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}
      <CameraCapture open={cameraOpen} onOpenChange={setCameraOpen} onCapture={upload} busy={busy} />
    </div>
  );
};
